import type { InnspelStorage, MySubmission } from '@innspel/core';
import { notiserAv } from './grunnlag.ts';

/**
 * F14 (brukerreisene 2026-09-15) — «Saken med nytt svar ligger ikke øverst og er ikke
 * merket.» Produkteier svarte i panelet; i appen sto feilen nummer to, under et ønske uten
 * nytt svar, og ingenting sa at det var kommet noe.
 *
 * Løftet er at alle får svar. Et svar innsenderen ikke oppdager, er et brutt løfte — og
 * lista er sortert på når saken ble SENDT (serveren: `OrderByDescending(CreatedAt)`), som
 * ikke sier noe om når det skjedde noe med den.
 *
 * **Hva «nytt» betyr her.** Plattformen eier aldri sluttbrukere, og skal ikke vite hva hun
 * har lest. «Nytt» er derfor «kommet siden forrige gang DENNE enheten viste lista»: ett
 * tidspunkt i lagringen verten allerede injiserer, ingen serverstate, ingen pseudonym, og
 * ingenting som forlater enheten. Bytter hun telefon, ser hun svarene som nye én gang til
 * — en ærlig pris for ikke å spore mennesker.
 *
 * **Hvorfor en kopi og ikke `@innspel/core`.** Kjernen har et hardt tak på 10 kB gzip
 * (CLAUDE.md), og modulen dyttet den over. En visningsregel er ikke verdt kjernens budsjett;
 * originalen bor i `@innspel/react-native`, og dette er de samme linjene. Endres regelen,
 * endres begge — det er prisen for taket, og den er lavere enn å bryte det.
 *
 * Forkastet: sortering på svartidspunkt alene, uten merke — da ville et svar fra i fjor
 * ligget øverst for alltid. Forkastet: å la serveren avgjøre — den måtte lagret «sett» per
 * pseudonym, altså nettopp det plattformen ikke skal vite.
 */

const NØKKEL = 'innspel:svar-sett';

/**
 * En rad med om noe på den er kommet siden sist enheten viste lista: svaret (F14) eller en
 * notis «Hva vi fant ut» (H3.1, P4). `nytt` er «ett av dem»; de to andre sier hvilket, så
 * merket kan hete «Nytt svar» om svaret og bare «Nytt» om notisen — en notis er aldri et
 * svar (DD-27).
 */
export interface MedNyhet {
  rad: MySubmission;
  nytt: boolean;
  nyttSvar: boolean;
  nyNotis: boolean;
}

/**
 * Radene med noe nytt først, hver merket. Rekkefølgen ellers er serverens — nyest sendt
 * først, som fortsatt er riktig for alt uten nytt. Køede rader har verken svar eller notis
 * og er aldri nye. En ødelagt eller uleselig lagring gir «alt er nytt»: heller vise at det
 * finnes noe enn å gjemme det.
 */
export async function merkNyeSvar(rader: readonly MySubmission[], storage: InnspelStorage): Promise<MedNyhet[]> {
  let grense = 0;
  try {
    grense = Number.parseInt((await storage.get(NØKKEL)) ?? '', 10) || 0;
  } catch {
    grense = 0;
  }
  const merket = rader.map((rad) => {
    const nyttSvar = rad.pending !== true && Date.parse(rad.reply?.at ?? '') > grense;
    const nyNotis = notiserAv(rad).some((n) => Date.parse(n.at) > grense);
    return { rad, nytt: nyttSvar || nyNotis, nyttSvar, nyNotis };
  });
  return [...merket.filter((m) => m.nytt), ...merket.filter((m) => !m.nytt)];
}

/**
 * Setter «sist sett» til nå. Kalles når innsenderen HAR sett lista — ikke når den hentes i
 * bakgrunnen for tellingen på knappen (DD-62), for da har hun ikke sett noe. Feiler
 * lagringen, vises merket én gang for mye; det er ikke verdt å avbryte visningen for.
 */
export async function markerSvarSett(storage: InnspelStorage, nå: number = Date.now()): Promise<void> {
  try {
    await storage.set(NØKKEL, String(nå));
  } catch {
    /* vertens lagring — ikke vår å redde */
  }
}
